<template>
  <section class="company-payment mt-1">
    <div class="text-center p-1">
      
      <div v-if="isFormPending">
        <p class="lead text-muted">To proceed with your accreditation application, please complete the Organizational Overview Form. Once submitted, our team will review your details and determine the appropriate accreditation fee.</p>
        <a :href="formLink" class="btn btn-primary btn-lg mt-3 shadow-sm">Complete Organizational Overview Form</a>
      </div>
      
      <div v-else-if="isPendingApproval">
        <p class="lead text-muted">Thank you for submitting your Organizational Overview Form. Our team is currently reviewing your information to determine your accreditation fee. You will receive a notification once your pricing is finalized. If you have any questions, feel free to contact us.</p>
      </div>
      
      <div v-else-if="isPaymentReadyToProcess && !isPaymentOnHold && !isPaymentCompleted">
        <p class="lead text-danger font-weight-bold mb-1">Your accreditation fee has been determined, and your payment is now ready for processing.</p>
        <p class="small text-muted font-weight-bold">Please add the below product to your cart, then proceed to the cart to checkout.</p>

        <div class="product-info card shadow-sm p-3 mt-4">
          <h4 class="text-primary">{{ product.product_name }}</h4>
          <p class="text-muted">Price: <strong>${{ product.product_price }}</strong></p>
        </div>
        
        <div class="d-flex justify-content-center gap-3 mt-4">
          <button v-if="itemCartedOrNowAdded" disabled class="btn btn-secondary shadow-sm mr-1">
            <i class="fa fa-check mr-05" aria-hidden="true"></i> Fee Added to Cart
          </button>
          <a v-else @click="addToCart" class="btn btn-primary shadow-sm mr-1">
            <i class="fa fa-plus mr-05" aria-hidden="true"></i> Add to Cart
          </a>
          <a @click="goToCart" class="btn btn-secondary shadow-sm">
            <i class="fa fa-arrow-right mr-05" aria-hidden="true"></i> Go to Cart
          </a>
        </div>
      </div>
      
      <div v-else-if="isPaymentOnHold">
        <div class="alert alert-info shadow-sm p-3 mt-4">
          <h4 class="text-primary"><i class="fa fa-info-circle mr-05" aria-hidden="true"></i> Payment Pending</h4>
          <p class="lead text-muted">Your payment is currently on-hold. <br> You will receive a confirmation email once your payment has been approved.</p>
          <p class="small text-muted mt-3" v-if="orderNumbers">{{ orderNumbers }}</p>
        </div>
      </div>

      <div v-else-if="isPaymentCompleted">
        <div class="alert alert-success shadow-sm p-3 mt-4">
          <h4 class="text-success"><i class="fa fa-check-circle mr-05" aria-hidden="true"></i> Payment Complete</h4>
          <p class="lead text-muted">Your accreditation fee has been successfully processed. <br>You now have full access to your application.</p>
          <p class="small text-muted mt-3" v-if="orderNumbers">{{ orderNumbers }}</p>
        </div>
      </div>
      
    </div>
  </section>
</template>

<script>
export default {
  props: {
    taskData: {
      type: Object,
      required: true
    },
    cartNonce: {
      type: String,
      required: true
    },
    cartUrl: {
      type: String,
      required: true
    },
    product: {
      type: Object,
      required: true
    },
    itemCarted: {
      type: Boolean,
      required: true
    },
    appId: {
      type: Number,
      required: false,
      default: null
    }
  },
  data() {
    return {
      cartSuccessfullyAdded: false
    }
  },
  computed: {
    itemCartedOrNowAdded() {
      return this.itemCarted || this.cartSuccessfullyAdded;
    },
    isFormPending() {
      return this.taskData.org_overview.status === 1;
    },
    isPendingApproval() {
      return this.taskData.org_overview.status === 2 && this.taskData.account_approval.status === 1;
    },
    isPaymentReadyToProcess() {
      return this.taskData.account_approval.status === 2;
    },
    isPaymentOnHold() {
      return this.taskData.payment_status && this.taskData.payment_status.status === 3;
    },
    isPaymentCompleted() {
      return this.taskData.payment_status && this.taskData.payment_status.status === 2;
    },
    formLink() {
      return this.taskData.org_overview.data.link.length > 0 ? this.taskData.org_overview.data.link[0].link : '#';
    },
    orderNumbers() {
      if (!this.taskData.payment_status || !this.taskData.payment_status.data.orders) {
        return null;
      }
      const orders = this.taskData.payment_status.data.orders;
      if (orders.length === 0) {
        return null;
      }
      if (orders.length === 1) {
        return `Order #${orders[0]}`;
      }
      return `Orders #${orders.join(', #')}`;
    }
  },
  methods: {
    addToCart() {
      // Always use add_to_cart_with_app with the app_id
      if (!this.appId) {
        toastr.error('Application ID is required', 'Error');
        return;
      }

      const data = {
        nonce: this.cartNonce,
        product_id: this.product.product_id,
        app_id: this.appId
      };

      $.ajax({
        url: localized.ajaxurl + 'add_to_cart_with_app',
        method: 'POST',
        dataType: 'json',
        data: data,
        success: (result) => {
          if (result.success) {
            toastr.success('Product added to cart', 'Success');
            this.cartSuccessfullyAdded = true;
          } else {
            toastr.error(result.message, 'Error');
          }
        },
        error: function() {
          toastr.error('Error adding product to cart', 'Error');
        }
      });
    },
    goToCart() {
      // Redirect to WooCommerce cart
      window.location.href = this.cartUrl;
    }
  }
};
</script>

<style scoped>
.alert {
  border-radius: 4px;
}
.text-success {
  color: #1ab394;
}
.text-primary {
  color: #1ab394;
}
.mr-1 {
  margin-right: 0.25rem;
}
.mr-05 {
  margin-right: 0.125rem;
}
</style>